<template>
  <h1>Hi {{ user.first_name }} 👋</h1>
  <p class="subtitle">Welcome to your {{ provider.name }} portal</p>


  <div class="account_info cols less-spacing">
    <div class="col">
      <div class="name">Account number</div>
      <div class="value">{{ pretty_account_id }}</div>
    </div>
    <div class="col">
      <div class="name">Phone support</div>
      <div class="value"><a :href="'tel:'+provider.subscriber_support_phone">{{
          formatPhoneNumber(provider.subscriber_support_phone)
        }}</a></div>
    </div>
    <div class="col">
      <div class="name">Email support</div>
      <div class="value"><a :href="'mailto:'+provider.subscriber_support_email">{{
          provider.subscriber_support_email
        }}</a></div>
    </div>
  </div>

  <h2>Your services</h2>

  <template v-if="services_loading">
    <div class="service">
      <div class="body">
        <div class="name">
          <span class="loading_string"></span>
        </div>
        <div class="location">
          <span class="loading_string"></span>
        </div>
      </div>
    </div>
  </template>

  <div v-if="services.length === 0">
    You don't have any services yet 😢
  </div>

  <div v-else v-for="service in mainServices" :key="service.id" class="service">
    <div class="body">
      <div class="name">
        {{ service.product.name }}
      </div>

      <div class="id">
        {{ service.id }}
      </div>
      <div class="facts">
        <div class="fact">
          Perfect home internet
        </div>
        <div class="fact">
          {{ service.product.upload_speed_mbps }}Mbps Upload
        </div>
        <div class="fact">
          {{ service.product.download_speed_mbps }}Mbps Download
        </div>
      </div>
      <div v-if="service.location" class="location">
        {{ service.location.full_name }}
      </div>

      <div :class="['status',service.status]">
        {{ service.status }}
      </div>
    </div>

    <div class="actions" v-if="service.status=='PREPARING' || service.status=='READY'">
      <template v-if="service.status=='PREPARING' && service.checkout_id">
        <router-link style="margin:0.5em;" :to="{name:'order',params:{checkoutId:service.checkout_id}}"
          class="btn-a btn-small">View progress
        </router-link>
      </template>
      <template v-if="service.status=='READY'">
        <router-link v-if="service.checkout_id" style="margin:0.5em;"
                     :to="{name:'order',params:{checkoutId:service.checkout_id}}" class="btn-subtle-outline btn-small">
          Original order
        </router-link>
      </template>
    </div>

    <div class="actions" v-if="service.available_addons.length">
      <div class="stack">
        <h3>Available add-ons</h3>

        <ApplyAddOnPlan 
          v-for="(productAddon, index) in service.available_addons"
          :key="index"
          :productAddon="productAddon"
          :hidePlans="true"
        />

        <div class="row">
          <router-link :to="`/apply/addons?parentService=${service.id}`" class="btn-a">
            View and purchase add-on plans
          </router-link>
        </div>
      </div>
    </div>

  </div>

  <h2 v-if="addonServices.length > 0">Your add-on plans</h2>
  <template v-if="services_loading">
    <div class="service">
      <div class="body">
        <div class="name">
          <span class="loading_string"></span>
        </div>
        <div class="location">
          <span class="loading_string"></span>
        </div>
      </div>
    </div>
  </template>

  <div v-else-if="addonServices.length > 0" v-for="service in addonServices" :key="service.id" class="service">
    <div class="body">
      <small v-if="service.parent_service"><strong>Plan:</strong> {{ service.parent_service.product.name }}</small>
      <div class="name">
        {{ service.product.name }}
      </div>

      <div class="id">
        {{ service.id }}
      </div>

      <div v-if="service.location" class="location">
        {{ service.location.full_name }}
      </div>

      <div :class="['status',service.status]">
        {{ service.status }}
      </div>
    </div>

    <div class="actions">
      <template v-if="service.status === 'PREPARING' && service.checkout_id">
        <router-link style="margin:0.5em;" :to="{name:'order',params:{checkoutId:service.checkout_id}}"
                     class="btn-a btn-small">View progress
        </router-link>
      </template>
      <template v-if="service.status === 'READY'">
        <router-link v-if="service.checkout_id" style="margin:0.5em;"
                     :to="{name:'order',params:{checkoutId:service.checkout_id}}" class="btn-subtle-outline btn-small">
          Original order
        </router-link>
      </template>
    </div>

  </div>


</template>


<script>

import {ref} from "vue";
import useActiveProviderStore from "../../stores/ActiveProviderStore";
import useAuthStore from "../../stores/AuthStore";
import ApplyAddOnPlan from "./Apply/ApplyAddOnPlan.vue";

export default {

  components: { ApplyAddOnPlan },

  setup() {
    const {provider} = useActiveProviderStore();
    const {user, account, logout} = useAuthStore();
    return {
      services_loading: ref(false),
      services: ref([]),
      user,
      account,
      provider,
      logout,
    };
  },

  mounted() {
    this.getServices();
  },

  computed: {
    pretty_account_id() {
      if (!this.account) return;
      return this.account.id.toString().replace(/\d{4}(?=.)/g, '$& ');
    },
    addonServices() {
      return this.services.filter((service) => {
        return service.product.type !== 'internet_service';
      });
    },
    mainServices() {
      return this.services.filter((service) => {
        return service.product.type === 'internet_service';
      });
    }
  },

  methods: {
    getServices() {

      this.services_loading = true;
      if (this.account) {
        axios.get('services?account_id=' + this.account.id).then(({data}) => {
          this.services = data.data;
          this.services_loading = false;
        });
      }
    },
    formatPhoneNumber(x) {
      return x.replace('+44', '0').replace(/(\d{4})(\d{3})(\d{4})/, '$1 $2 $3');
      ;
    },
  }


}
</script>

<style lang="scss" scoped>

.account_info {
  padding: 2em;
  color: #FFF;
  background: #222;
  border-radius: 0.4em;
  margin-bottom: 2em;

  .col {
    .name {
      font-size: 0.8em;
      color: rgb(172, 172, 172);
      text-transform: uppercase;
      margin-bottom: 0.2em;
      letter-spacing: 0.05em;;
    }

    .value {
      font-size: 1.2em;
      font-weight: bold;

      a {
        text-decoration: none;
      }
    }
  }
}

h1 {
  font-size: 2.5em;
}

.service {
  border: 1px solid #CCC;
  border-radius: 0.4em;
  overflow: hidden;

  .body,
  .actions {
    padding: 1.5em;
    position: relative;
  }

  .actions {
    padding-top: 1em;
    background: #f5f5f5;
  }

  .status {
    position: absolute;
    background: #555;
    color: #FFF;
    padding: 0.6em 1em;
    border-radius: 0.5em;
    top: 0.5em;
    right: 0.5em;
    font-size: 0.7em;
    letter-spacing: 0.05em;

    &.READY,
    &.ACTIVE {
      background: rgb(21, 160, 21);
    }


    &.PREPARING {
      background: rgb(18, 193, 247);
    }

  }

  .name {
    font-size: 1.8em;
    font-weight: bold;

  }

  .id {
    position: absolute;
    top: 2.2rem;
    right: 0.5em;
    color: #777;
    font-size: 0.75em;
    letter-spacing: 0.02em;

    &::before {
      content: 'REF #'
    }
  }

  .facts {
    display: flex;
    color: #555;

    .fact + .fact {
      margin-left: 0.5em;

      &::before {
        content: '•';
        margin-right: 0.5em;
      }
    }
  }

  .location {
    margin-top: 0.4em;
  }

  & + .service {
    margin-top: 1em;
  }
}
</style>